import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { Action } from '../action'
import { ActionService } from '../action.service'

@Component({
  selector: 'app-action-detail',
  templateUrl: './action-detail.component.html',
  styleUrls: ['./action-detail.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneSubtitleComponent,
    ElderPaneContentComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ActionDetailComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private actionService = inject(ActionService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _action$: BehaviorSubject<Action> = new BehaviorSubject(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.action = this.route.snapshot.data['action']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.action) {
      this.action = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get action$(): Observable<Action> {
    return this._action$.asObservable()
  }

  public get action(): Action {
    return this._action$.getValue()
  }

  @Input()
  public set action(action: Action) {
    if (action) {
      this._creationMode = !action.id

      this._action$.next(action)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.action.id) {
      this.actionService.findById(this.action.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.actionService.update(this.action).subscribe({
      next: (updated) => {
        this.action = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.actionService.delete(this.action).subscribe({
      next: (_) => this.close.emit(),
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): Action {
    return <Action>{}
  }
}
